<template>
	<div class="pa-2">
		<B-confirm-edit-dialog
			ref="dialog"
			@on-consent="addEmployee"
		></B-confirm-edit-dialog>

		<v-row dense>
			<!-- experiences -->
			<v-col cols="12">
				<v-card
					height="100%"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-2">
						{{ $T('experiences.title') }}
					</strong>
					<bee-handy-smart-table
						ref="experienceTable"
						v-model="experience.inputs"
						:headers="experience.headers"
						:items="experience.items"
						@keypress.enter="experience.editId === null ? experience.add() : experience.update()"
						:items-per-page="-1"
						:data-table-props="{ disableSort: true }"
						:readonly-input-fields="inputsAttr.tableDisable"
						dense
						elevation="0"
						editable-rows
						hide-no-data-area
						show-add-row
						hide-default-footer
						fixed-header
						hide-inputs-details
						stay-in-input-fields>
						<template v-slot:input.companyName="{ on, attr }">
							<b-async-input :storeName="'filters/fetchCompanyFilters'">
								<template v-slot:default="{ items, loading, focus }">
									<v-combobox
										:items="items"
										:loading="loading"
										item-text="name"
										v-on="on"
										v-bind="attr"
										@focus="focus"></v-combobox>
								</template>
							</b-async-input>
						</template>
						<template v-slot:item.companyName="{ item }">
							{{ item.companyName?.name || item.companyName }}
						</template>
						<template v-slot:item.actions="{ item, index }">
							<div class="d-flex">
								<tooltip
									:text="$T('tooltips.edit')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										depressed
										rounded
										icon
										small
										@click="
											experience.ref.activeEditForRow(index);
											experience.editId = experience.items.findIndex(
												(e) => e.id === item.id,
											);
										">
										<v-icon
											size="20"
											color="edit-color">
											$edit
										</v-icon>
									</v-btn>
								</tooltip>
								<tooltip
									:text="$T('tooltips.delete')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										icon
										small
										@click="experience.delete(index)">
										<v-icon
											size="20"
											color="alert">
											$delete
										</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
						<template v-slot:no-input.actions="{}">
							<tooltip
								:text="$T('tooltips.addRow')"
								:disabled="inputsAttr.tableDisable">
								<v-btn
									:disabled="inputsAttr.tableDisable"
									icon
									small
									color="success"
									@click="
										experience.editId === null ? experience.add() : experience.update()
									">
									<v-icon
										size="20"
										color="addRow">
										$addRow
									</v-icon>
								</v-btn>
							</tooltip>
						</template>
					</bee-handy-smart-table>
				</v-card>
			</v-col>

			<!-- expectations -->
			<v-col
				cols="12"
				md="5">
				<v-card
					height="100%"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-1">{{ $T('expectations.jobYouWant') }}</strong>
					<!-- multiple -->
					<v-select
						v-model="expectedJob"
						:items="jobsItems"
						:loading="loadingOldJob"
						:disabled="loadingOldJob"
						multiple
						item-text="name"
						item-value="id"
						class="mb-2"
						dense
						small-chips
						chips
						outlined
						hide-details
						:label="$T('expectations.jobName')"
						v-bind="inputsAttr">
					</v-select>
					<strong class="d-block mb-1">{{ $T('expectations.expectSalary') }}</strong>
					<div class="d-flex mb-2">
						<v-text-field
							v-model="expectedSalaryMin"
							class="me-2"
							outlined
							dense
							hide-details
							:label="$T('from')"
							v-bind="inputsAttr">
						</v-text-field>
						<v-text-field
							v-model="expectedSalaryMax"
							outlined
							dense
							hide-details
							:label="$T('to')"
							v-bind="inputsAttr">
						</v-text-field>
					</div>
					<table>
						<tr>
							<td class="pb-1">{{ $T('expectations.haveRelativesWork') }}</td>
							<td class="pb-1">
								<v-radio-group
									v-model="haveRelativesWork"
									class="mt-1"
									row
									dense
									hide-details
									v-bind="inputsAttr">
									<v-radio
										:label="$T('yes')"
										:value="true"></v-radio>
									<v-radio
										:label="$T('no')"
										:value="false"></v-radio>
								</v-radio-group>
							</td>
						</tr>
						<tr>
							<td class="pb-1">{{ $T('expectations.workNight') }}</td>
							<td class="pb-1">
								<v-radio-group
									v-model="workNight"
									class="mt-1"
									row
									dense
									hide-details
									v-bind="inputsAttr">
									<v-radio
										:label="$T('yes')"
										:value="true"></v-radio>
									<v-radio
										:label="$T('no')"
										:value="false"></v-radio>
								</v-radio-group>
							</td>
						</tr>
						<tr>
							<td class="pb-1">{{ $T('expectations.extraWorkOnVacations') }}</td>
							<td class="pb-1">
								<v-radio-group
									v-model="extraWorkOnVacations"
									class="mt-1"
									row
									dense
									hide-details
									v-bind="inputsAttr">
									<v-radio
										:label="$T('yes')"
										:value="true"></v-radio>
									<v-radio
										:label="$T('no')"
										:value="false"></v-radio>
								</v-radio-group>
							</td>
						</tr>
					</table>
				</v-card>
			</v-col>

			<!-- recommend -->
			<v-col
				cols="12"
				md="7">
				<v-card
					height="100%"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-2">
						{{ $T('recommend.title') }}
					</strong>
					<bee-handy-smart-table
						ref="recommendTable"
						v-model="recommend.inputs"
						:headers="recommend.headers"
						:items="recommend.items"
						@keypress.enter="recommend.editId === null ? recommend.add() : recommend.update()"
						:items-per-page="-1"
						:data-table-props="{ disableSort: true }"
						:readonly-input-fields="inputsAttr.tableDisable"
						dense
						elevation="0"
						editable-rows
						hide-no-data-area
						show-add-row
						hide-default-footer
						fixed-header
						hide-inputs-details
						stay-in-input-fields>
						<template v-slot:input.company="{ on, attr }">
							<b-async-input :storeName="'filters/fetchCompanyFilters'">
								<template v-slot:default="{ items, loading, focus }">
									<v-combobox
										:items="items"
										:loading="loading"
										item-text="name"
										v-on="on"
										v-bind="attr"
										@focus="focus"></v-combobox>
								</template>
							</b-async-input>
						</template>
						<template v-slot:item.company="{ item }">
							{{ item.company?.name || item.company }}
						</template>
						<template v-slot:item.actions="{ item, index }">
							<div class="d-flex">
								<tooltip
									:text="$T('tooltips.edit')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										depressed
										rounded
										icon
										small
										@click="
											recommend.ref.activeEditForRow(index);
											recommend.editId = recommend.items.findIndex(
												(e) => e.id === item.id,
											);
										">
										<v-icon
											size="20"
											color="edit-color">
											$edit
										</v-icon>
									</v-btn>
								</tooltip>
								<tooltip
									:text="$T('tooltips.delete')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										icon
										small
										@click="recommend.delete(index)">
										<v-icon
											size="20"
											color="alert">
											$delete
										</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
						<template v-slot:no-input.actions="{}">
							<tooltip
								:text="$T('tooltips.addRow')"
								:disabled="inputsAttr.tableDisable">
								<v-btn
									:disabled="inputsAttr.tableDisable"
									icon
									small
									color="success"
									@click="recommend.editId === null ? recommend.add() : recommend.update()">
									<v-icon
										size="20"
										color="addRow">
										$addRow
									</v-icon>
								</v-btn>
							</tooltip>
						</template>
					</bee-handy-smart-table>
				</v-card>
			</v-col>

			<v-col cols="12">
				<v-textarea
					v-model="notes"
					:label="$T('inputs.notes')"
					rows="3"
					outlined
					dense
					hide-details
					auto-grow
					v-bind="inputsAttr"></v-textarea>
			</v-col>
		</v-row>
		<v-divider class="mt-4 mb-3"></v-divider>
		<div class="d-flex">
			<v-btn
				color="primary"
				:to="{ name: 'academic-qualifications', query: { employeeId: $route.query.employeeId } }">
				<v-icon left> mdi-share </v-icon>
				{{ $T('default-buttons.prev') }}
			</v-btn>
			<v-spacer></v-spacer>
			<v-btn
				class="px-10 mx-2"
				@click="$refs.dialog.activeConfirmDialog()"
				color="success"
				:loading="loadingAddingEmployee || loadingFetchEmployee"
				:disabled="loadingAddingEmployee || loadingFetchEmployee || disableSave">
				{{ employeeId ? 'تعديل ملف الموظف' : 'إنشاء ملف الموظف' }}
			</v-btn>
			<v-btn
				color="primary"
				:to="{ name: 'employee-attachments', query: { employeeId: $route.query.employeeId } }">
				{{ $T('default-buttons.next') }}
				<v-icon right> mdi-reply </v-icon>
			</v-btn>
		</div>
	</div>
</template>

<script>
import moment, { DATE_FORMAT } from '@/utils/moment';
import { TableLocale, showErrorMessagesFun } from '@/utils/helpers';
import BConfirmEditDialog from '@/components/forms/BConfirmEditDialog'

export default {
	name: 'PracticalExperiences',
	components: { BConfirmEditDialog },
	props: {
		loadingFetchEmployee: { type: Boolean },
		loadingAddingEmployee: { type: Boolean },
		employeeInfo: { type: Object, default: () => ({}) },
		employeeId: { type: String },
		editingEmployee: { type: Boolean, default: false },
		disableSave: { type: Boolean, default: false },
	},

	data() {
		const experienceHeaders = [
			{ text: this.$T('experiences.jobName'), name: 'jobName', value: 'jobName', sortable: false },
			{
				text: this.$T('experiences.companyName'),
				name: 'companyName',
				value: 'companyName',
				sortable: false,
			},
			{ text: this.$T('experiences.address'), name: 'address', value: 'address', sortable: false },
			{
				text: this.$T('experiences.startDate'),
				name: 'startDate',
				value: 'startDate',
				type: 'date',
				dateFormat: DATE_FORMAT,
				sortable: false,
				width: `${100 / 7}%`,
			},
			{
				text: this.$T('experiences.endDate'),
				name: 'endDate',
				value: 'endDate',
				type: 'date',
				dateFormat: DATE_FORMAT,
				width: `${100 / 7}%`,
				sortable: false,
			},
			{
				text: this.$T('experiences.quitRaison'),
				name: 'quitRaison',
				value: 'quitRaison',
				sortable: false,
			},
			{ text: this.$T('experiences.salary'), name: 'salary', value: 'salary', sortable: false },
			{
				text: '',
				name: 'actions',
				value: 'actions',
				noInput: true,
				width: 20,
			},
		];
		const experienceValidation = [
			{ require: 'jobName', message: this.$T('validations.nameRequire') },
			{ require: 'companyName', message: this.$T('validations.companyRequire') },
			{ require: 'startDate', message: this.$T('validations.startDateRequire') },
			{ require: 'endDate', message: this.$T('validations.endDateRequire') },
			{ require: 'quitRaison', message: this.$T('validations.quitRaisonRequire') },
			{
				for: ['startDate', 'endDate'],
				fun: (s, e) => moment(e).isAfter(s),
				message: this.$T('validations.datesOrder'),
			},
		];
		const experience = new TableLocale(experienceHeaders, {
			validation: experienceValidation,
			showErrorMessages: true,
			showErrorMessagesFun: showErrorMessagesFun(this),
		});

		const recommendHeaders = [
			{ text: this.$T('recommend.name'), name: 'name', value: 'name', sortable: false },
			{ text: this.$T('recommend.job'), name: 'job', value: 'job', sortable: false },
			{ text: this.$T('recommend.company'), name: 'company', value: 'company', sortable: false },
			{ text: this.$T('recommend.phone'), name: 'phone', value: 'phone', sortable: false },
			{
				text: '',
				name: 'actions',
				value: 'actions',
				noInput: true,
				width: 20,
			},
		];
		const recommendValidation = [
			{ require: 'name', message: this.$T('validations.recommend.nameRequire') },
			{ require: 'job', message: this.$T('validations.recommend.jobRequire') },
			{ require: 'company', message: this.$T('validations.recommend.companyRequire') },
		];
		const recommend = new TableLocale(recommendHeaders, {
			validation: recommendValidation,
			showErrorMessages: true,
			showErrorMessagesFun: showErrorMessagesFun(this),
		});

		return {
			loadingOldJob: true,

			experience,
			recommend,

			notes: null,

			expectedJob: [],
			expectedSalaryMin: null,
			expectedSalaryMax: null,
			haveRelativesWork: null,
			workNight: null,
			extraWorkOnVacations: null,

			jobsItems: [],
		};
	},

	watch: {
		employeeInfo() {
			this.setInfo();
		},
	},

	computed: {
		inputsAttr() {
			return {
				readonly: this.editingEmployee,
				disabled: this.editingEmployee,
				tableDisable: this.editingEmployee,
			};
		},
	},

	methods: {
		setInfo() {
			if (this.employeeInfo.experience) {
				this.experience.items = [];
				this.employeeInfo.experience.forEach((e, i) => {
					this.experience.items.push({ ...e, id: ++this.experience.counter });
				});
			}
			if (this.employeeInfo.recommend) {
				this.recommend.items = [];
				this.employeeInfo.recommend.forEach((e, i) => {
					this.recommend.items.push({ ...e, id: ++this.recommend.counter });
				});
			}

			this.employeeInfo.expectedJob && (this.expectedJob = this.employeeInfo.expectedJob);

			this.expectedSalaryMin = this.employeeInfo.expectedSalaryMin;
			this.expectedSalaryMax = this.employeeInfo.expectedSalaryMax;

			this.haveRelativesWork = this.employeeInfo.haveRelativesWork;
			this.workNight = this.employeeInfo.workNight;
			this.extraWorkOnVacations = this.employeeInfo.extraWorkOnVacations;

			this.notes = this.employeeInfo.practicalNote;
		},
		getInfos() {
			return {
				data: {
					experience: this.experience.items,
					recommend: this.recommend.items,
					expectedJob: this.expectedJob,
					expectedSalaryMin: this.expectedSalaryMin,
					expectedSalaryMax: this.expectedSalaryMax,
					haveRelativesWork: this.haveRelativesWork,
					workNight: this.workNight,
					extraWorkOnVacations: this.extraWorkOnVacations,
					practicalNote: this.notes,
				},
			};
		},
		addEmployee() {
			this.$refs.dialog.deactivateConfirmDialog()
			this.$emit('add-employee', this.getInfos().data);
		},
	},

	mounted() {
		this.$nextTick(() => {
			this.experience.ref = this.$refs.experienceTable;
			this.recommend.ref = this.$refs.recommendTable;
		});
		this.setInfo();

		// todo move to BAsyncInput
		this.$store
			.dispatch('filters/fetchJobDescriptions')
			.then((res) => {
				this.jobsItems = res;
			})
			.finally(() => {
				this.loadingOldJob = false;
			});
	},
};
</script>
